import type { FC } from 'react';
import { useEffect, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import classNames from 'classnames';
import { calculateProgress, useAnimatedProgress } from 'hooks/useAnimatedProgress';
import { FilePreview, useDocumentPreview } from 'hooks/useDocumentPreview';

import { Analytics } from 'services/analytics';
import { FileUploadingModalViewTypes } from 'services/analytics/events';

import { updateDownloadProgress } from 'data/actions/documents';
import { documentByIdSelector, uploadProgressSelector } from 'data/selectors/documents';
import { modalOptionsSelector } from 'data/selectors/modals';

import ModalLayout from '../baseModal/base';
import arrow from './assets/arrow.svg';
import check from './assets/check.svg';
import ProgressCircle from './assets/progressCircle';

const ProgressEditFileModalMobileEditor30: FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const options = useSelector(modalOptionsSelector);
  const dataToConvert = options?.dataToConvert;
  const downloadProgress = useSelector(uploadProgressSelector());
  const animatedProgress = useAnimatedProgress(5);
  const progressValue = calculateProgress(animatedProgress, 0, 90);
  const [barValue, setBarValue] = useState(0);
  const [visibleSteps, setVisibleSteps] = useState<number[]>([]);

  const eventsMap = useMemo(
    () =>
      new Map([
        [10, 'file_uploading_10'],
        [35, 'file_uploading_35'],
        [70, 'file_uploading_70'],
        [100, 'file_uploading_100']
      ]),
    []
  );

  useEffect(() => {
    if (progressValue > downloadProgress) {
      dispatch(updateDownloadProgress(progressValue));
    }
  }, [dispatch, progressValue, downloadProgress]);

  useEffect(() => {
    return () => {
      dispatch(updateDownloadProgress(0));
    };
  }, [dispatch]);

  useEffect(() => {
    const newBarValue = Number(downloadProgress.toFixed(0));
    setBarValue(newBarValue);
  }, [downloadProgress]);

  useEffect(() => {
    Analytics.sendEvent({
      event: 'file_uploading_modal_view',
      data: { type: FileUploadingModalViewTypes.editFunnels }
    });
  }, []);

  useEffect(() => {
    const event = eventsMap.get(barValue);
    if (event) {
      Analytics.sendEvent({ event });
    }
  }, [barValue, eventsMap]);

  const shortFileName = (fileName: string, format: string) => {
    if (!fileName) return '';

    const extension = format.toLowerCase();
    if (fileName.length > 20) {
      const firstPart = fileName.slice(0, 5);
      const lastPart = fileName.replace(/\.[^.]+$/, '').slice(-5);
      return `${firstPart}....${lastPart}.${extension}`;
    }
    return fileName.replace(/\.[^.]+$/, `.${extension}`);
  };

  const documentById = useSelector(documentByIdSelector());
  const originalFileFormat = dataToConvert?.from;
  const convertedFileFormat = dataToConvert?.to;
  const originalFileName = shortFileName(documentById?.name || dataToConvert?.filename, originalFileFormat);
  const exportFileName = shortFileName(dataToConvert?.filename, convertedFileFormat);

  const { image } = useDocumentPreview({
    file: useMemo(() => ({ ...dataToConvert, internal_type: dataToConvert?.from }), [dataToConvert]),
    fileUrl: dataToConvert?.url,
    formatTo: dataToConvert?.to,
    formatFrom: dataToConvert?.from,
    width: 150,
    isDownloadFile: true
  });

  const steps = [
    t('popups.converting_with_progress_popup.stages.first'),
    t('popups.converting_with_progress_popup.stages.second'),
    t('popups.converting_with_progress_popup.stages.third')
  ];

  useEffect(() => {
    const stepInterval = 2000;
    setVisibleSteps([0]);
    const timer1 = setTimeout(() => setVisibleSteps((prev) => [...prev, 1]), stepInterval);
    const timer2 = setTimeout(() => setVisibleSteps((prev) => [...prev, 2]), stepInterval * 2);
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, []);

  const renderFileFormat = (readyFileFormat: string) => (
    <div
      className={classNames(
        'absolute top-4 -left-1 py-0.5 px-3 text-white rounded font-extrabold z-10 text-[13px]',
        {
          'bg-[#192CD2]': readyFileFormat === 'DOCX' || readyFileFormat === 'DOC'
        },
        {
          'bg-[#E25F49]': readyFileFormat === 'PNG' || readyFileFormat === 'PPTX'
        },
        {
          'bg-[#008554]': readyFileFormat === 'EPUB' || readyFileFormat === 'XLS' || readyFileFormat === 'XLSX'
        },
        {
          'bg-[#A110B9]': readyFileFormat === 'JPG' || readyFileFormat === 'JPEG'
        },
        { 'bg-[#D2294B]': readyFileFormat === 'PDF' }
      )}
      data-testid='destination-file-format'
    >
      {readyFileFormat}
    </div>
  );

  const renderHeadline = () => {
    return (
      <div className='flex items-center justify-center pt-8 text-[#1D1D1D] text-center text-[25px] font-black leading-[130%]'>
        {t('popups.progress_file_mobile_editor.headline', {
          format: convertedFileFormat
        })}
      </div>
    );
  };

  return (
    <ModalLayout>
      <div className='flex flex-col items-center gap-6 tablet:w-[468px] w-full min-w-[calc(100%-32px)]'>
        <h2 className='text-[#373737] text-[22px] leading-[24px] font-bold text-center mb-2'>{renderHeadline()}</h2>
        <div className='flex flex-row items-center gap-1 w-full justify-center px-4'>
          <div className='flex flex-col items-center max-w-[130px]'>
            <div className='relative w-[130px] h-[170px] overflow-visible flex items-center justify-center rounded-sm shadow-[0px_1px_3px_0px_rgba(166,175,195,0.60)] shadow-t-[0px_-3px_5px_rgba(0,0,0,0.25)]'>
              {renderFileFormat(originalFileFormat)}
              <div className='h-full object-cover'>
                <FilePreview
                  src={image}
                  width={130}
                />
              </div>
            </div>
            <div className='mt-2 text-center w-full'>
              <div className='text-[#878787] text-[14.5px] truncate'>{originalFileName}</div>
            </div>
          </div>
          <img
            src={arrow}
            alt='arrow'
            className='text-[32px] text-[#666] mx-2'
          />
          <div className='flex flex-col items-center max-w-[130px]'>
            <div className='relative w-[130px] h-[170px] overflow-visible flex items-center justify-center shadow-md'>
              <div className='h-full object-cover blurred'>
                <FilePreview
                  src={image}
                  width={130}
                />
              </div>
              <div className='absolute inset-0 flex items-center justify-center bg-black/20 backdrop-blur-[1.5px] rounded-sm'>
                <div className='w-20 h-20 relative'>
                  <ProgressCircle progress={barValue} />
                </div>
              </div>
              {renderFileFormat(convertedFileFormat)}
            </div>
            <div className='mt-2 text-center w-full'>
              <div className='text-[#616161] text-center font-nunito text-[14.5px] leading-[135%] truncate'>
                {exportFileName}
              </div>
            </div>
          </div>
        </div>

        <ul className='w-full mt-4 list-disc list-inside text-[14px] text-[#1D1D1D] bg-[#F5F5F5] p-4'>
          {steps.map((step, idx) => (
            <li
              key={idx}
              className={`flex mb-4 transition-opacity duration-500 font-nunito text-[18px] font-semibold leading-[125%] text-[#1D1D1D] ${
                visibleSteps.includes(idx) ? 'opacity-100' : 'opacity-0 invisible'
              }`}
            >
              <img
                src={check}
                alt='check icon'
                className='mr-2 w-4 h-4'
              />
              {step}
            </li>
          ))}
        </ul>
      </div>
    </ModalLayout>
  );
};

export default ProgressEditFileModalMobileEditor30;
